'use client';

import { whitelabelConfig } from '@/brands';
import {
  Button,
  Checkbox,
  FormField,
  InputForm,
  Select,
  type SelectOption,
  SocialNameInfoTooltip,
  formatPhone,
} from '@/components';
import { Controller } from '@/libs';
import { formatCEP, normalizeSocialNameInput } from '@/utils';

import { useEditUserProfile } from '../hooks/useEditUserProfile';

const marketingOptInLabel =
  whitelabelConfig.checkoutCheckboxes?.marketingOptIn?.label ||
  'Aceito receber comunicações de marketing e ofertas';

export const EditUserProfileModal = () => {
  const {
    control,
    errors,
    isValid,
    isLoadingProfile,
    isSearchingCep,
    isSubmitting,
    handleCepBlur,
    handleClose,
    handleSubmit,
    statesOptions,
    watchHasSocialName,
  } = useEditUserProfile();

  const sectionTitleClassName = 'text-xs font-semibold leading-none text-foreground/70';
  const fieldLabelClassName = 'text-xs font-normal leading-[1] text-foreground/70';
  const checkboxLabelClassName = 'text-xs font-normal leading-none text-foreground/70';
  const inputClassName =
    'h-9 rounded-[6px] border-2 border-black/25 bg-white px-3 text-xs font-normal text-black/50 placeholder:text-black/45';
  const readOnlyInputClassName =
    'h-9 rounded-[6px] border-2 border-black/25 bg-[#EFEFEF] px-3 text-xs font-normal text-black/50 placeholder:text-black/45';

  return (
    <div
      data-cy="edit-user-profile-modal"
      className="flex h-full w-full min-h-0 flex-col overflow-hidden bg-background text-foreground md:h-auto md:max-h-[94vh] md:w-[min(100vw-2rem,56rem)] md:rounded-2xl"
    >
      <div className="relative border-b border-foreground/10 px-4 py-3 md:px-6">
        <h2 className="text-center text-lg font-semibold">Minhas Informações</h2>
        <Button
          data-cy="edit-user-profile-modal-close-button"
          type="button"
          variant="ghost"
          size="small"
          icon="Close"
          className="absolute right-3 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-foreground/8 text-foreground hover:bg-foreground/12"
          onClick={handleClose}
          disabled={isSubmitting}
        />
      </div>

      <form
        data-cy="edit-user-profile-modal-form"
        onSubmit={handleSubmit}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-6">
          <div className="flex flex-col gap-5">
            <section className="space-y-3">
              <h3 className={sectionTitleClassName}>Informações pessoais</h3>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  error={errors.fullname}
                  label="Nome"
                  labelClassName={fieldLabelClassName}
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-name-input"
                    control={control}
                    name="fullname"
                    placeholder="Nome"
                    readOnly
                    disabled
                    className={readOnlyInputClassName}
                  />
                </FormField>

                <FormField
                  error={errors.document}
                  label="CPF"
                  labelClassName={fieldLabelClassName}
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-document-input"
                    control={control}
                    name="document"
                    placeholder="000.000.000-00"
                    readOnly
                    disabled
                    className={readOnlyInputClassName}
                  />
                </FormField>

                <div
                  data-cy="edit-user-profile-modal-has-social-name-checkbox"
                  className="flex items-center sm:col-span-2"
                >
                  <Controller
                    control={control}
                    name="hasSocialName"
                    render={({ field }) => (
                      <Checkbox
                        checked={Boolean(field.value)}
                        onCheckedChange={field.onChange}
                        label="Informar nome social"
                        size="small"
                        className="gap-1 py-0"
                        labelClassName={checkboxLabelClassName}
                      />
                    )}
                  />
                  <SocialNameInfoTooltip />
                </div>

                {watchHasSocialName && (
                  <FormField
                    error={errors.socialName}
                    label="Nome social"
                    labelClassName={fieldLabelClassName}
                    className="sm:col-span-2"
                  >
                    <InputForm
                      data-cy="edit-user-profile-modal-social-name-input"
                      control={control}
                      name="socialName"
                      placeholder="Nome social"
                      formatFunction={normalizeSocialNameInput}
                      disabled={isLoadingProfile || isSubmitting}
                      className={inputClassName}
                    />
                  </FormField>
                )}

                <FormField
                  error={errors.phone}
                  label="Telefone"
                  required
                  labelClassName={fieldLabelClassName}
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-phone-input"
                    control={control}
                    name="phone"
                    rules={{ deps: ['phoneConfirmation'] }}
                    placeholder="(00) 00000-0000"
                    formatFunction={formatPhone}
                    inputMode="numeric"
                    autoComplete="tel"
                    disabled={isLoadingProfile || isSubmitting}
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  error={errors.phoneConfirmation}
                  label="Confirmar Telefone"
                  required
                  labelClassName={fieldLabelClassName}
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-confirm-phone-input"
                    control={control}
                    name="phoneConfirmation"
                    rules={{ deps: ['phone'] }}
                    placeholder="(00) 00000-0000"
                    formatFunction={formatPhone}
                    inputMode="numeric"
                    autoComplete="tel"
                    disabled={isLoadingProfile || isSubmitting}
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  error={errors.email}
                  label="Email"
                  required
                  labelClassName={fieldLabelClassName}
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-email-input"
                    control={control}
                    name="email"
                    rules={{ deps: ['confirmEmail'] }}
                    placeholder="email@email.com"
                    inputMode="email"
                    autoComplete="email"
                    disabled={isLoadingProfile || isSubmitting}
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  error={errors.confirmEmail}
                  label="Confirmar Email"
                  required
                  labelClassName={fieldLabelClassName}
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-confirm-email-input"
                    control={control}
                    name="confirmEmail"
                    rules={{ deps: ['email'] }}
                    placeholder="email@email.com"
                    inputMode="email"
                    autoComplete="email"
                    disabled={isLoadingProfile || isSubmitting}
                    className={inputClassName}
                  />
                </FormField>

                <div
                  data-cy="edit-user-profile-modal-marketing-opt-in-checkbox"
                  className="flex items-center sm:col-span-2"
                >
                  <Controller
                    control={control}
                    name="marketingOptIn"
                    render={({ field }) => (
                      <Checkbox
                        checked={Boolean(field.value)}
                        onCheckedChange={field.onChange}
                        label={marketingOptInLabel}
                        size="small"
                        className="gap-1 py-0"
                        labelClassName={checkboxLabelClassName}
                        disabled={isLoadingProfile || isSubmitting}
                      />
                    )}
                  />
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className={sectionTitleClassName}>Endereço</h3>

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  error={errors.zipCode}
                  label="CEP"
                  required
                  labelClassName={fieldLabelClassName}
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-zip-code-input"
                    control={control}
                    name="zipCode"
                    placeholder="00000-000"
                    formatFunction={formatCEP}
                    inputMode="numeric"
                    onBlur={handleCepBlur}
                    disabled={isLoadingProfile || isSubmitting || isSearchingCep}
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  error={errors.state}
                  label="Estado"
                  required
                  labelClassName={fieldLabelClassName}
                >
                  <Controller
                    control={control}
                    name="state"
                    render={({ field }) => (
                      <div data-cy="edit-user-profile-modal-state-select">
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Estado - UF"
                          disabled={isLoadingProfile || isSubmitting || isSearchingCep}
                          options={statesOptions as SelectOption[]}
                          className="w-full"
                          triggerClassName={inputClassName}
                        />
                      </div>
                    )}
                  />
                </FormField>

                <FormField
                  error={errors.city}
                  label="Cidade"
                  required
                  labelClassName={fieldLabelClassName}
                  className="col-span-2"
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-city-input"
                    control={control}
                    name="city"
                    placeholder="Cidade"
                    disabled={isLoadingProfile || isSubmitting || isSearchingCep}
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  error={errors.neighborhood}
                  label="Bairro"
                  required
                  labelClassName={fieldLabelClassName}
                  className="col-span-2"
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-neighborhood-input"
                    control={control}
                    name="neighborhood"
                    placeholder="Bairro"
                    disabled={isLoadingProfile || isSubmitting || isSearchingCep}
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  error={errors.street}
                  label="Logradouro"
                  required
                  labelClassName={fieldLabelClassName}
                  className="col-span-2"
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-street-input"
                    control={control}
                    name="street"
                    placeholder="Rua/Avenida"
                    disabled={isLoadingProfile || isSubmitting || isSearchingCep}
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  error={errors.number}
                  label="Número"
                  required
                  labelClassName={fieldLabelClassName}
                  className="col-span-2 sm:col-span-1"
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-number-input"
                    control={control}
                    name="number"
                    placeholder="123"
                    disabled={isLoadingProfile || isSubmitting}
                    className={inputClassName}
                  />
                </FormField>

                <FormField
                  error={errors.complement}
                  label="Complemento (opcional)"
                  labelClassName={fieldLabelClassName}
                  className="col-span-2 sm:col-span-1"
                >
                  <InputForm
                    data-cy="edit-user-profile-modal-complement-input"
                    control={control}
                    name="complement"
                    placeholder="Opcional"
                    disabled={isLoadingProfile || isSubmitting}
                    className={inputClassName}
                  />
                </FormField>
              </div>
            </section>
          </div>
        </div>

        <div className="border-t border-foreground/10 bg-background px-4 py-3 md:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              data-cy="edit-user-profile-modal-back-button"
              type="button"
              variant="ghost"
              size="large"
              className="!h-14 flex-1 rounded bg-foreground/10 text-base font-semibold text-foreground hover:bg-foreground/15 sm:!h-10 sm:text-sm"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Voltar
            </Button>

            <Button
              data-cy="edit-user-profile-modal-submit-button"
              type="submit"
              variant="primary"
              size="large"
              className="!h-14 flex-1 rounded border-0 bg-[#34c759] text-base font-semibold text-white sm:hover:bg-[#2fbb52] sm:!h-10 sm:text-sm"
              disabled={!isValid || isLoadingProfile || isSearchingCep}
              loading={isSubmitting}
              loadingText="Salvando"
            >
              Confirmar
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
